import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { isKeycloakConfigured } from '../auth/keycloak'

/** Signed-in user, their roles, sign-out — and, for an admin, branding. */
export function UserMenu() {
  const { me, logout, hasRole } = useAuth()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (!me) return null

  const label = me.displayName || me.userName
  const initials = label
    .split(/[\s.@_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('')

  return (
    <div ref={containerRef} className="relative">
      <button
        className="flex items-center gap-2 rounded px-1.5 py-1 text-[12.5px]"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span
          className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold"
          style={{ background: 'var(--color-primary)', color: 'var(--color-on-primary)' }}
          aria-hidden
        >
          {initials}
        </span>
        <span style={{ color: 'var(--color-muted)' }}>{label}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1 min-w-60 rounded border p-3"
          style={{
            background: 'var(--color-panel)',
            // See OrgSwitcher: no shadows in v4.
            borderColor: 'var(--color-ink)',
          }}
        >
          <div className="mb-2">
            <div className="text-[12.5px] font-medium">{label}</div>
            {me.email && (
              <div className="ident" style={{ color: 'var(--color-faint)' }}>
                {me.email}
              </div>
            )}
          </div>

          <div className="mb-3">
            <div className="col-head mb-1">roles</div>
            {me.roles.length === 0 ? (
              <span
                className="badge"
                style={{ background: 'var(--color-warn-wash)', color: 'var(--color-warn)' }}
                title="You have no Tracer roles, so every screen will be read-only or refused."
              >
                none assigned
              </span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {me.roles.map((role) => (
                  <span
                    key={role}
                    className="badge"
                    style={{ background: 'var(--color-accent-wash)', color: 'var(--color-accent)' }}
                  >
                    {role}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/*
              Settings, not Branding.

              Branding used to be reached from here on the reasoning that it is configured
              once per customer and does not deserve a navigation slot. That was right
              about branding and wrong as a pattern: every other setting got placed by the
              same logic, each somewhere different, and the product ended up with no answer
              to "where do I change things?".

              This menu is about WHO you are -- your name, your roles, signing out. The
              settings behind it belong to the organization, not to the person signed in,
              so they have a navigation slot of their own now. The link stays because
              somebody who learned the old route should land somewhere useful.
          */}
          {hasRole('Admin') && (
            <Link
              to="/settings"
              className="btn btn-sm mb-2 w-full"
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              Settings
            </Link>
          )}

          {isKeycloakConfigured() ? (
            <button className="btn btn-sm w-full" onClick={logout}>
              Sign out
            </button>
          ) : (
            <p className="text-[11px]" style={{ color: 'var(--color-warn)' }}>
              Authentication is disabled in this environment.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
